import { t } from "@lingui/macro";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ScrollArea,
  Separator,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useRef } from "react";

import { Copyright } from "@/client/components/copyright";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useIsCoachOptimizing } from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

import { CoachConfirmBar } from "../../_components/coach/coach-confirm-bar";
import { CoachScorePanel } from "../../_components/coach/coach-score-panel";
import { CoachUnloadWarning } from "../../_components/coach/coach-unload-warning";
import { ExportSection } from "./sections/export";
import { InformationSection } from "./sections/information";
import { LayoutSection } from "./sections/layout";
import { NotesSection } from "./sections/notes";
import { PageSection } from "./sections/page";
import { RtsScoreSection } from "./sections/rts-score";
import { SharingSection } from "./sections/sharing";
import { StatisticsSection } from "./sections/statistics";
import { TemplateSection } from "./sections/template";
import { ThemeSection } from "./sections/theme";
import { TypographySection } from "./sections/typography";
import { SectionIcon } from "./shared/section-icon";

export const RightSidebar = () => {
  const containterRef = useRef<HTMLDivElement | null>(null);
  const currentTemplate = useResumeStore((state) => state.resume.data?.metadata?.template ?? "");
  const isMastersUnion = currentTemplate === "mastersunion";
  const resumeId = useResumeStore((state) => state.resume.id);
  const isCoachOptimizing = useIsCoachOptimizing(resumeId);

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="flex bg-secondary-accent/30">
      <ScrollArea orientation="vertical" className="h-screen flex-1 pb-16 lg:pb-0">
        {/* Extra headroom during a coach optimization only — the score donut is the first thing in
            this rail and needs room above it. */}
        <div
          ref={containterRef}
          className={cn("grid gap-y-6 p-6 @container/right", isCoachOptimizing && "pt-12")}
        >
          {/* Coach Resume optimization: baseline vs projected score, pinned above the
              usual panels so it is the first thing read. The existing sections below
              are untouched. */}
          {isCoachOptimizing && (
            <>
              <CoachScorePanel />
              <CoachConfirmBar />
              {/* Renders a one-time modal in a portal — the position in this tree is irrelevant, it
                  is mounted here because this block is already gated on an active optimization. It
                  also carries the `beforeunload` guard, so the two cannot be mounted separately: the
                  browser's own "Leave site?" prompt cannot show custom text, and a prompt with no
                  explanation is exactly as unhelpful as an explanation with no prompt. */}
              <CoachUnloadWarning />
              <Separator />
            </>
          )}

          {isMastersUnion ? (
            <Accordion type="multiple" defaultValue={["export"]} className="-mt-2">
              <AccordionItem value="export">
                <AccordionTrigger className="text-left text-base font-medium">
                  {t`Export`}
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <ExportSection />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="layout">
                <AccordionTrigger className="text-left text-base font-medium">
                  {t`Layout`}
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <LayoutSection />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="typography">
                <AccordionTrigger className="text-left text-base font-medium">
                  {t`Typography`}
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <TypographySection />
                </AccordionContent>
              </AccordionItem>

              {/* <AccordionItem value="rts-score">
                <AccordionTrigger className="text-left text-base font-medium">
                  ATS Score
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <RtsScoreSection />
                </AccordionContent>
              </AccordionItem> */}

              <AccordionItem value="statistics">
                <AccordionTrigger className="text-left text-base font-medium">
                  {t`Statistics`}
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <StatisticsSection />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="sharing">
                <AccordionTrigger className="text-left text-base font-medium">
                  {t`Sharing`}
                </AccordionTrigger>
                <AccordionContent className="[&_section>header]:hidden">
                  <SharingSection />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          ) : (
            <>
              <TemplateSection />
              <Separator />
              <LayoutSection />
              <Separator />
              <TypographySection />
              <Separator />
              <ThemeSection />
              <Separator />
              <PageSection />
              <Separator />
              <SharingSection />
              <Separator />
              <StatisticsSection />
              <Separator />
              <RtsScoreSection />
              <Separator />
              <ExportSection />
              <Separator />
              <NotesSection />
              {/* <Separator />
          <InformationSection /> */}
              {/* <Separator />
          <Copyright className="text-center" /> */}
            </>
          )}
        </div>
      </ScrollArea>

      <div
        className={cn(
          "hidden basis-12 flex-col items-center justify-between py-4 sm:flex",
          isMastersUnion
            ? "dark:bg-[#101013] bg-[#f0f0f0] px-2 absolute right-0 top-0 h-full z-20"
            : "bg-secondary-accent/30",
        )}
      >
        <div />

        {isMastersUnion ? (
          <div className="flex flex-col items-center justify-center gap-y-2">
            <SectionIcon id="export" name={t`Export`} onClick={() => scrollIntoView("#export")} />
            <SectionIcon id="layout" name={t`Layout`} onClick={() => scrollIntoView("#layout")} />
            <SectionIcon
              id="typography"
              name={t`Typography`}
              onClick={() => scrollIntoView("#typography")}
            />
            <SectionIcon
              id="statistics"
              name={t`Statistics`}
              onClick={() => scrollIntoView("#statistics")}
            />
            <SectionIcon
              id="sharing"
              name={t`Sharing`}
              onClick={() => scrollIntoView("#sharing")}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-y-2">
            <SectionIcon
              id="template"
              name={t`Template`}
              onClick={() => scrollIntoView("#template")}
            />
            <SectionIcon id="layout" name={t`Layout`} onClick={() => scrollIntoView("#layout")} />
            <SectionIcon
              id="typography"
              name={t`Typography`}
              onClick={() => scrollIntoView("#typography")}
            />
            <SectionIcon id="theme" name={t`Theme`} onClick={() => scrollIntoView("#theme")} />
            <SectionIcon id="page" name={t`Page`} onClick={() => scrollIntoView("#page")} />
            <SectionIcon
              id="sharing"
              name={t`Sharing`}
              onClick={() => scrollIntoView("#sharing")}
            />
            <SectionIcon
              id="statistics"
              name={t`Statistics`}
              onClick={() => scrollIntoView("#statistics")}
            />
            <SectionIcon id="export" name={t`Export`} onClick={() => scrollIntoView("#export")} />
            <SectionIcon id="notes" name={t`Notes`} onClick={() => scrollIntoView("#notes")} />
            {/* <SectionIcon
            id="information"
            name={t`Information`}
            onClick={() => scrollIntoView("#information")}
          /> */}
          </div>
        )}

        <ThemeSwitch size={14} />
      </div>
    </div>
  );
};
